import React, { createContext, JSX, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth0, User as Auth0User } from '@auth0/auth0-react'
import logger from 'logger'
import { AnalyticsMetafield } from 'types/api'
import { graphqlRequest } from 'lib/helpers'
import getClient from '../graphql/getClient.graphql'
import getClients from '../graphql/getClients.graphql'

const log = logger.extend('context')

type AnalyticsRole = 'admin' | 'analyst' | 'finance'
export interface ExtendedUser extends Auth0User {
  admin: boolean
  clientIds?: string[]
}
export interface ClientOptions {
  products: AnalyticsMetafield[]
  bundles: AnalyticsMetafield[]
  projects: AnalyticsMetafield[]
}

export interface AnalyticsUser {
  name?: string
  email?: string
  clients: AnalyticsMetafield[]
  role?: AnalyticsRole
}

export interface AnalyticsClient {
  id: string
  title: string
}

interface AnalyticsMetafieldsByClient {
  products: AnalyticsMetafield[]
  projects: AnalyticsMetafield[]
  bundles: AnalyticsMetafield[]
}

export interface AuthContextType {
  isAuthenticated: boolean
  analyticsUser: AnalyticsUser | null
  selectedClient: AnalyticsMetafield | null
  setSelectedClientId: React.Dispatch<React.SetStateAction<string | undefined>>
  auth0Token: string | undefined
  clientOptions?: ClientOptions
}

const defaultUser: AnalyticsUser = {
  name: '',
  email: '',
  role: undefined,
  clients: [{
    id: '',
    title: ''
  }]
}

const defaultContext: AuthContextType = {
  isAuthenticated: false,
  analyticsUser: defaultUser,
  selectedClient: null,
  setSelectedClientId: (): void => undefined,
  auth0Token: undefined,
  clientOptions: { products: [], projects: [], bundles: [] }
}

export const CLIENT_STORAGE_KEY = 'analytics_client_id'

export const AuthContext = createContext<AuthContextType>(defaultContext)
AuthContext.displayName = 'Auth Context'

export const UserProvider = ({ children }): JSX.Element => {
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0()

  const [analyticsUser, setAnalyticsUser] = useState<AnalyticsUser | null>(null)
  const [selectedClient, setSelectedClient] = useState<AnalyticsMetafield | null>(null)
  const [selectedClientId, setSelectedClientId] = useState<string>()
  const [auth0Token, setAuth0Token] = useState<string>()
  const [clientOptions, setClientOptions] = useState<ClientOptions>()

  log({ func: 'UserProvider', children })

  // Fetch user data (token, clients) after login
  useEffect(() => {
    if (!isAuthenticated || user == null) return

    const initializeUser = async (): Promise<void> => {
      const token = await getAccessTokenSilently()
      setAuth0Token(token)
      const extendedUser = user as ExtendedUser
      const roleMap = [
        { condition: extendedUser.admin, role: 'admin' as AnalyticsRole },
        { condition: extendedUser.analyst, role: 'analyst' as AnalyticsRole },
        { condition: extendedUser.finance, role: 'finance' as AnalyticsRole }
      ]
      const role = roleMap.find(r => r.condition)?.role

      const newUser: AnalyticsUser = {
        email: extendedUser.email,
        name: extendedUser.name,
        role,
        clients: []
      }

      try {
        const { clients } = await graphqlRequest<{ clients: AnalyticsClient[] }>(
          getClients,
          {},
          token
        )
        newUser.clients = clients
      } catch (error: any) {
        log(error)
      }

      setAnalyticsUser(newUser)
      if (newUser.clients.length > 0) {
        const storedClientId = globalThis.localStorage.getItem(CLIENT_STORAGE_KEY)
        const isValidStoredId = newUser.clients.some(c => c.id === storedClientId)
        setSelectedClientId(isValidStoredId ? storedClientId as string : newUser.clients[0].id)
      }
    }

    void initializeUser()
  }, [isAuthenticated, user])

  // Fetch products and client info when selected client changes
  useEffect(() => {
    if (selectedClientId === undefined || auth0Token === undefined || analyticsUser == null) return

    const fetchProductsAndClient = async (): Promise<void> => {
      const { analyticsMetafields } = await graphqlRequest<{ analyticsMetafields: AnalyticsMetafieldsByClient }>(
        getClient,
        { clientId: selectedClientId },
        auth0Token
      )
      setClientOptions(analyticsMetafields)

      const newClient = analyticsUser.clients.find(client => client.id === selectedClientId)
      if (newClient !== undefined) {
        setSelectedClient(newClient)
        globalThis.localStorage.setItem(CLIENT_STORAGE_KEY, selectedClientId)
      }
    }

    void fetchProductsAndClient()
  }, [selectedClientId, auth0Token, analyticsUser])

  return (
    <AuthContext.Provider
      value={useMemo(() => ({
        isAuthenticated,
        analyticsUser,
        selectedClient,
        clientOptions,
        setSelectedClientId,
        auth0Token
      }), [isAuthenticated, analyticsUser, selectedClient, clientOptions, setSelectedClientId, auth0Token])}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => useContext(AuthContext)
